'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { VerifyOtpResponse, validateToken } from '../api/authApi';

// Define the auth state type
interface AuthState {
  isAuthenticated: boolean;
  user: {
    id?: number;
    phone_number?: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    profile_picture?: string | null;
  } | null;
  token: string | null;
  isLoading: boolean;
}

// Define the auth context type
interface AuthContextType extends AuthState {
  login: (userData: VerifyOtpResponse['data']) => void;
  logout: () => void;
  checkAuthStatus: () => Promise<boolean>;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider props
interface AuthProviderProps {
  children: ReactNode;
}

// Local storage keys
const AUTH_TOKEN_KEY = 'auth_token';
const USER_DATA_KEY = 'user_data';
const TOKEN_VALIDATION_KEY = 'token_last_validated';
// Time before revalidating (5 minutes in milliseconds)
const VALIDATION_INTERVAL = 5 * 60 * 1000;

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize auth state from local storage
  const [authState, setAuthState] = useState<AuthState>(() => {
    if (typeof window === 'undefined') {
      return { isAuthenticated: false, user: null, token: null, isLoading: true };
    }
    
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const userData = localStorage.getItem(USER_DATA_KEY);
      
      if (token && userData) {
        const user = JSON.parse(userData);
        return { isAuthenticated: true, user, token, isLoading: true };
      }
    } catch (error) {
      console.error('Error loading auth state from localStorage:', error);
    }
    
    return { isAuthenticated: false, user: null, token: null, isLoading: false };
  });

  // Update local storage when auth state changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (authState.isAuthenticated && authState.token && authState.user) {
      localStorage.setItem(AUTH_TOKEN_KEY, authState.token);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(authState.user));
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      localStorage.removeItem(TOKEN_VALIDATION_KEY);
    }
  }, [authState.isAuthenticated, authState.token, authState.user]);

  // Validate token on initial load
  useEffect(() => {
    const validateSession = async () => {
      if (!authState.token) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Check if we've validated recently
      const lastValidated = localStorage.getItem(TOKEN_VALIDATION_KEY);
      const now = Date.now();
      
      if (lastValidated && (now - parseInt(lastValidated, 10) < VALIDATION_INTERVAL)) {
        // Skip validation if done recently
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const profileData = await validateToken();
        
        if (profileData) {
          // Update user data with latest profile information
          setAuthState(prev => ({ 
            ...prev, 
            isAuthenticated: true,
            user: {
              id: profileData.data.user_id,
              phone_number: profileData.data.phone_number,
              first_name: profileData.data.first_name,
              last_name: profileData.data.last_name,
              email: profileData.data.email,
              profile_picture: profileData.data.profile_picture
            },
            isLoading: false
          }));
          
          // Set last validation timestamp
          localStorage.setItem(TOKEN_VALIDATION_KEY, now.toString());
        } else {
          // Token is invalid, logout the user
          setAuthState({ 
            isAuthenticated: false, 
            user: null, 
            token: null,
            isLoading: false
          });
        }
      } catch (error) {
        console.error('Session validation error:', error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    // Validate on initial load
    validateSession();

    // Set up periodic validation (every 15 minutes)
    const intervalId = setInterval(validateSession, 15 * 60 * 1000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [authState.token]);

  // Function to check auth status on demand
  const checkAuthStatus = async (): Promise<boolean> => {
    if (!authState.token) return false;
    
    try {
      const profileData = await validateToken(true); // Skip cache
      
      if (profileData) {
        // Update user data with latest profile information
        setAuthState(prev => ({ 
          ...prev, 
          isAuthenticated: true,
          user: {
            id: profileData.data.user_id,
            phone_number: profileData.data.phone_number,
            first_name: profileData.data.first_name,
            last_name: profileData.data.last_name,
            email: profileData.data.email,
            profile_picture: profileData.data.profile_picture
          }
        }));
        
        // Set last validation timestamp
        localStorage.setItem(TOKEN_VALIDATION_KEY, Date.now().toString());
        return true;
      } else {
        // Token is invalid, logout the user
        logout();
        return false;
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      return false;
    }
  };

  // Login function - saves user data and token
  const login = (userData: VerifyOtpResponse['data']) => {
    const { token, user_id, ...user } = userData;
    setAuthState({
      isAuthenticated: true,
      user: { id: user_id, ...user },
      token,
      isLoading: false
    });
    
    // Set validation timestamp
    localStorage.setItem(TOKEN_VALIDATION_KEY, Date.now().toString());
  };

  // Logout function - clears user data and token
  const logout = () => {
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false
    });
  };

  // Create the context value
  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 